import { useState } from 'react';
import { useTranslation } from '@/contexts/i18n-context';
import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs/text-input';
import { Form } from '@/components/form';
import { Modal, ModalBody, ModalButtonContainer } from '@/components/modal/modal';
import { SuccessCircleSvg } from '@/components/svgs/success-circle';
import { useCreateMarketingSubscriptions } from '@/api/save-email-for-marketing';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useBranding } from '@/outlets/branding-outlet';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { zt } from '@/util/validators';
import clsx from 'clsx';
import styles from './marketing-notifications-banner.module.css';

const subscriptionSchema = z.object({
  email: z.string().email(zt('common.errors.email')),
});

type FormValues = z.infer<typeof subscriptionSchema>;

export function MarketingNotificationsBanner() {
  const { displayMarketingNotificationsBanner } = useBranding();
  const { mutate: saveEmail } = useCreateMarketingSubscriptions();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('--screen-md');

  const [isOpenMarketingModal, setIsOpenMarketingModal] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(subscriptionSchema),
    reValidateMode: 'onChange',
  });

  const onSubmit = (data: FormValues) => {
    saveEmail(
      { data },
      {
        onSuccess: () => {
          setIsOpenMarketingModal(true);
          form.setValue('email', '');
        },
      },
    );
  };

  if (!displayMarketingNotificationsBanner) {
    return null;
  }

  return (
    <>
      <Form form={form} onSubmit={onSubmit} className={styles.marketingBanner}>
        <div className={styles.marketingBannerText}>
          <h1 className="h3">{t('dashboard.marketingBanner.title')}</h1>
          <p className={clsx(!isMobile && 'body-1')}>{t('dashboard.marketingBanner.subTitle')}</p>
        </div>
        <TextInput
          inputClassName={styles.emailInput}
          className={styles.emailInputContainer}
          name="email"
          aria-label={t('dashboard.marketingBanner.emailLabel')}
          placeholder={t('dashboard.marketingBanner.placeholder')}
          withIconsIndicators={false}
        />
        <Button fullWidth type="submit">
          {t('dashboard.marketingBanner.buttonTxt')}
        </Button>
      </Form>

      <Modal
        className={styles.modal}
        isOpen={isOpenMarketingModal}
        closeOnOutsideClick
        onClose={() => setIsOpenMarketingModal(false)}
      >
        <SuccessCircleSvg className={styles.modalIcon} />
        <h2 className="h3">{t('dashboard.marketingModal.title')}</h2>
        <ModalBody>{t('dashboard.marketingModal.body')}</ModalBody>
        <ModalButtonContainer className={styles.buttonContainer}>
          <Button onClick={() => setIsOpenMarketingModal(false)} rightIcon={'arrow_forward'}>
            {t('common.continue')}
          </Button>
        </ModalButtonContainer>
      </Modal>
    </>
  );
}
